// assets/custom-hero-banner.js
(function () {
  'use strict';

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const instances = new Map();

  function debounce(fn, delay = 150) {
    let timer;
    return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), delay); };
  }

  // ── Hero (parallax + slide carousel) ────────────────────
  class Hero {
    constructor(el) {
      this.el      = el;
      this.slides  = [...el.querySelectorAll('[data-hero-slide]')];
      this.dots    = [...el.querySelectorAll('[data-hero-dot]')];
      this.current = 0;
      this.timer   = null;
      this.autoplay = el.dataset.heroAutoplay === 'true';
      this.interval = parseInt(el.dataset.heroInterval, 10) || 6000;
      this.swipe    = el.dataset.heroSwipe === 'true';
      this.slidesEl = el.querySelector('[data-hero-slides]');
      this._onScroll = null;

      this._initParallax();
      this._initFit();
      if (this.slides.length > 1) this._initCarousel();
    }

    // ── Fit the copy inside the image's text safe zone ───────────────────────
    // The hero image templates reserve a band for text (desktop: left 45%,
    // mobile: top 40%). That band is a fixed fraction of the image, so on a phone
    // it can be ~200 CSS px — less than the configured type needs. The copy renders
    // at its configured size and we measure the real height; if it exceeds the band
    // we scale the whole block down until it fits. No text is ever truncated.
    //
    // Timing is the tricky part (this is what caused "button clipped on first load,
    // fine after clicking"): the first measurement must happen AFTER layout and the
    // display web font have settled, or the block is measured short (fallback font,
    // fewer wraps), fit=1 is accepted, then the real font reflows taller and spills
    // out of the band. So we refit on: next frame after init, fonts.ready, every
    // in-hero image load, each slide change, and any band resize (rotation/resize).
    _initFit() {
      if (this.el.dataset.heroFit !== 'true') return;

      this.fitTargets = this.slides
        .map(slide => ({
          wrap:    slide.querySelector('.hbk-hero__content-wrap'),
          content: slide.querySelector('.hbk-hero__content')
        }))
        .filter(t => t.wrap && t.content);
      if (!this.fitTargets.length) return;

      this.minFit = parseFloat(this.el.dataset.heroFitMin) || 0.5;

      this._onResize = debounce(() => this._fitAll(), 150);
      window.addEventListener('resize', this._onResize, { passive: true });
      window.addEventListener('orientationchange', this._onResize, { passive: true });

      // Refit when the band itself changes size (viewport, rotation, editor reflow).
      // Observe the WRAP, not the content: the wrap's height is independent of the
      // scale we apply, so this can't feed back into an infinite resize loop.
      if ('ResizeObserver' in window) {
        this._ro = new ResizeObserver(() => this._fitAll());
        this.fitTargets.forEach(t => this._ro.observe(t.wrap));
      }

      // First fit after the next frame, so initial layout is committed.
      this._raf = requestAnimationFrame(() => this._fitAll());

      // Web fonts change line metrics after first paint — refit once they land,
      // on the following frame so the reflow they trigger is already measured.
      document.fonts?.ready
        .then(() => requestAnimationFrame(() => this._fitAll()))
        .catch(() => {});

      // Late-loading images inside the content (trust badges, guarantee icon) push
      // the block taller after the initial fit — refit as each one loads.
      this.el.querySelectorAll('.hbk-hero__content img').forEach(img => {
        if (!img.complete) img.addEventListener('load', this._onResize, { once: true });
      });
    }

    _fitAll() {
      this.fitTargets?.forEach(t => this._fitOne(t));
    }

    _fitOne({ wrap, content }) {
      content.style.setProperty('--hero-fit', '1');

      const cs = getComputedStyle(wrap);
      const available =
        wrap.clientHeight - parseFloat(cs.paddingTop || 0) - parseFloat(cs.paddingBottom || 0);
      if (available <= 0) return;

      // Fast path: already fits at full size.
      if (content.scrollHeight <= available) return;

      // Binary search the largest scale that fits. Height is monotonic in the
      // scale, so ~7 probes land within <1% of the true maximum.
      let lo = this.minFit;
      let hi = 1;
      let best = this.minFit;
      for (let i = 0; i < 7; i++) {
        const mid = (lo + hi) / 2;
        content.style.setProperty('--hero-fit', String(mid));
        if (content.scrollHeight <= available) {
          best = mid;
          lo = mid;
        } else {
          hi = mid;
        }
      }
      content.style.setProperty('--hero-fit', String(best));
    }

    // ── Parallax (single-slide heroes only) ──
    _initParallax() {
      if (this.el.dataset.heroParallax !== 'true' || prefersReducedMotion) return;
      const mediaWrap = this.el.querySelector('.hbk-hero__media-wrap');
      if (!mediaWrap) return;

      let ticking = false;
      const update = () => {
        const rect = this.el.getBoundingClientRect();
        if (rect.bottom < 0 || rect.top > window.innerHeight) { ticking = false; return; }
        const progress = -rect.top / (rect.height + window.innerHeight);
        mediaWrap.style.transform = `translateY(${(progress * 60).toFixed(2)}px)`;
        ticking = false;
      };
      this._onScroll = () => { if (!ticking) { requestAnimationFrame(update); ticking = true; } };
      window.addEventListener('scroll', this._onScroll, { passive: true });
      update();
    }

    // ── Carousel ──
    _initCarousel() {
      this.dots.forEach((dot, i) => {
        dot.addEventListener('click', () => { this.goTo(i); this._restart(); });
      });

      // Pause on hover / focus so a reader isn't rushed.
      this.el.addEventListener('mouseenter', () => this._stop());
      this.el.addEventListener('mouseleave', () => this._start());
      this.el.addEventListener('focusin',  () => this._stop());
      this.el.addEventListener('focusout', () => this._start());

      // Keyboard arrows when a dot has focus.
      this._onKey = (e) => {
        if (e.key === 'ArrowLeft')  { this.goTo(this.current - 1); this._restart(); }
        if (e.key === 'ArrowRight') { this.goTo(this.current + 1); this._restart(); }
      };
      this.el.addEventListener('keydown', this._onKey);

      // Pause when the tab/section is off-screen.
      this._io = new IntersectionObserver((entries) => {
        entries.forEach(entry => entry.isIntersecting ? this._start() : this._stop());
      }, { threshold: 0.2 });
      this._io.observe(this.el);

      if (this.swipe) this._initSwipe();
      this._initClickNav();

      this._start();
    }

    // ── Click / tap to advance ──
    // A plain click or tap on the slide (anywhere that isn't the CTA, a link, the
    // dots, or a form field) advances to the next slide — on desktop and mobile
    // alike. Clicks that follow a drag are already swallowed by the swipe handler
    // (stopPropagation in the capture phase), so a drag never double-triggers this.
    _initClickNav() {
      if (!this.slidesEl) return;
      const INTERACTIVE = 'a, button, input, select, textarea, label, [role="button"], [data-hero-dot]';
      this._onSlideClick = (e) => {
        if (e.target.closest(INTERACTIVE)) return; // let the CTA / links / dots do their thing
        this.goTo(this.current + 1);
        this._restart();
      };
      this.slidesEl.addEventListener('click', this._onSlideClick);
    }

    // ── Swipe (touch) + drag (mouse) ──
    // Cross-fade carousel: no track to move, so we detect a horizontal gesture and
    // advance on release. `touch-action: pan-y` (CSS) lets vertical page scroll pass
    // through while horizontal gestures reach these handlers.
    _initSwipe() {
      const el = this.slidesEl;
      if (!el) return;
      let startX = 0, startY = 0, down = false, moved = false;
      const THRESHOLD = 40;

      const onDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return; // left button only
        down = true; moved = false;
        startX = e.clientX; startY = e.clientY;
      };
      const onMove = (e) => {
        if (!down) return;
        const dx = e.clientX - startX, dy = e.clientY - startY;
        if (!moved && Math.abs(dx) > 8 && Math.abs(dx) > Math.abs(dy)) {
          moved = true;
          el.classList.add('is-dragging');
        }
        // Prevent text selection / image drag while dragging with a mouse.
        if (moved && e.pointerType === 'mouse') e.preventDefault();
      };
      const onUp = (e) => {
        if (!down) return;
        down = false;
        el.classList.remove('is-dragging');
        const dx = e.clientX - startX, dy = e.clientY - startY;
        if (Math.abs(dx) >= THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
          this.goTo(this.current + (dx < 0 ? 1 : -1));
          this._restart();
        }
      };
      // Swallow the click that follows a drag so a dragged button isn't activated.
      const onClick = (e) => { if (moved) { e.preventDefault(); e.stopPropagation(); moved = false; } };
      const onDragStart = (e) => e.preventDefault();

      el.addEventListener('pointerdown', onDown);
      window.addEventListener('pointermove', onMove, { passive: false });
      window.addEventListener('pointerup', onUp);
      el.addEventListener('click', onClick, true);
      el.addEventListener('dragstart', onDragStart);
      this._swipe = { el, onDown, onMove, onUp, onClick, onDragStart };
    }

    goTo(index) {
      const n = this.slides.length;
      if (!n) return;
      index = ((index % n) + n) % n;
      this.slides.forEach((s, i) => s.classList.toggle('is-active', i === index));
      this.dots.forEach((d, i) => {
        const active = i === index;
        d.classList.toggle('is-active', active);
        d.setAttribute('aria-current', active ? 'true' : 'false');
      });
      this.current = index;

      // Re-fit the slide now shown, in case its band/copy settled after the initial
      // pass (belt-and-braces with the init/fonts/image/resize refits).
      if (this.fitTargets && this.fitTargets[index]) this._fitOne(this.fitTargets[index]);
    }

    next() { this.goTo(this.current + 1); }

    _start() {
      if (!this.autoplay || prefersReducedMotion || this.timer || this.slides.length < 2) return;
      this.timer = setInterval(() => this.next(), this.interval);
    }

    _stop() {
      if (this.timer) { clearInterval(this.timer); this.timer = null; }
    }

    _restart() { this._stop(); this._start(); }

    destroy() {
      this._stop();
      if (this._onScroll) window.removeEventListener('scroll', this._onScroll);
      if (this._onResize) {
        window.removeEventListener('resize', this._onResize);
        window.removeEventListener('orientationchange', this._onResize);
      }
      if (this._raf) cancelAnimationFrame(this._raf);
      this._ro?.disconnect();
      if (this._onKey) this.el.removeEventListener('keydown', this._onKey);
      if (this._onSlideClick) this.slidesEl?.removeEventListener('click', this._onSlideClick);
      this._io?.disconnect();
      if (this._swipe) {
        const s = this._swipe;
        s.el.removeEventListener('pointerdown', s.onDown);
        window.removeEventListener('pointermove', s.onMove);
        window.removeEventListener('pointerup', s.onUp);
        s.el.removeEventListener('click', s.onClick, true);
        s.el.removeEventListener('dragstart', s.onDragStart);
        this._swipe = null;
      }
    }
  }

  // ── Init / destroy ──────────────────────────────────────
  function init(scope = document) {
    scope.querySelectorAll('.hbk-hero').forEach(el => {
      if (instances.has(el)) return;
      instances.set(el, new Hero(el));
    });
  }

  function destroy(scope = document) {
    scope.querySelectorAll('.hbk-hero').forEach(el => {
      if (instances.has(el)) { instances.get(el).destroy(); instances.delete(el); }
    });
  }

  document.addEventListener('DOMContentLoaded', () => init());
  document.addEventListener('shopify:section:load',   e => init(e.target));
  document.addEventListener('shopify:section:unload', e => destroy(e.target));

})();
